import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AcuStorageService } from '../../service/acustorage.service';
import { GoogleAuthService } from '../../service/google-auth.service';
import { UserStore } from '../../store/user.store';

import { tap } from 'rxjs/operators';
import { DiagnosticsService } from '../diagnostics/diagnostics.service';

@Injectable({
  providedIn: 'root'
})
export class ApiInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private storage: AcuStorageService,
    private injector: Injector
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isJarvisSupport = req.url.startsWith(environment.jarvisApiUrl);
    const isAcuplaceApi = req.url.startsWith(environment.apiUrl);

    let modifiedReq = req;

    // Abilita l'invio automatico dei cookie per il nostro backend
    if (isAcuplaceApi || isJarvisSupport) {
      const jwtToken = this.storage.getJwtToken();
      let headers = req.headers;
      if (jwtToken && !headers.has('Authorization')) {
        headers = headers.set('Authorization', `Bearer ${jwtToken}`);
      }
      headers = headers.set('X-Acuplace-Version', environment.acuplaceVersion);
      modifiedReq = req.clone({
        withCredentials: false,
        headers: headers
      });
    }

    try {
      const userStore = this.injector.get(UserStore);
      const user = userStore.userLogged;
      if (user) {
        modifiedReq = modifiedReq.clone({
          headers: modifiedReq.headers
            .set('X-User-Id', user.id || '')
            .set('X-User-Roles', (user.roles || []).join(','))
        });
      }
    } catch (e) {
      // Prevent cyclic dependency warnings during early app boot
    }

    if (environment.production) {
      const logPrefix = isJarvisSupport ? '[Jarvis Request]' : '[API Request]';
      console.log(`${logPrefix} ${req.method} ${req.url}`, {
        headers: modifiedReq.headers.keys()
      });
    }

    const startTime = Date.now();

    return next.handle(modifiedReq).pipe(
      tap({
        next: (event: any) => {
          if (event && event.status) {
            this.recordHttpCall(req.method, req.url, event.status, Date.now() - startTime);
          }
        },
        error: (err: any) => {
          const status = err instanceof HttpErrorResponse ? err.status : 0;
          const errDetail = err?.error?.message || err?.message || 'Network error';
          this.recordHttpCall(req.method, req.url, status, Date.now() - startTime, errDetail);
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(modifiedReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private recordHttpCall(method: string, url: string, status: number, durationMs: number, error?: string): void {
    try {
      // Don't log replay upload loop if sending support
      if (url.includes('acuplace-support/create')) return;
      const diagnostics = this.injector.get(DiagnosticsService);
      diagnostics.logHttpCall({
        timestamp: new Date().toISOString(),
        method,
        url,
        status,
        durationMs,
        error
      });
    } catch (e) {
      // Diagnostics service may not yet be initialized
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const googleAuth = this.injector.get(GoogleAuthService);

      return from(googleAuth.refreshToken()).pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          
          let headers = request.headers;
          if (newToken) {
            headers = headers.set('Authorization', `Bearer ${newToken}`);
          }
          return next.handle(request.clone({ headers, withCredentials: false }));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((newToken) => {
          let headers = request.headers;
          if (newToken && !headers.has('Authorization')) {
            headers = headers.set('Authorization', `Bearer ${newToken}`);
          }
          return next.handle(request.clone({ headers, withCredentials: false }));
        })
      );
    }
  }
}
